import { Card, Row, Col, Button, Badge } from 'react-bootstrap'

const ReportsDashboard = ({ onSelectReport }) => {
  const reports = [
    {
      id: 'estado-resultados',
      title: 'Estado de Resultados',
      description: 'Muestra los ingresos, costos, gastos y utilidad neta de un período determinado.',
      icon: 'bi-graph-up-arrow',
      color: 'primary',
      category: 'Reporte Financiero',
      features: ['Ingresos y costos', 'Gastos operacionales', 'Utilidad neta', 'Exportación PDF/Excel']
    },
    {
      id: 'balance-general',
      title: 'Balance General',
      description: 'Presenta la situación financiera de la empresa en una fecha específica (Activos, Pasivos y Capital).',
      icon: 'bi-pie-chart',
      color: 'success',
      category: 'Reporte Financiero',
      features: ['Activos', 'Pasivos', 'Capital', 'Ecuación contable']
    },
    {
      id: 'balanza-comprobacion',
      title: 'Balanza de Comprobación',
      description: 'Lista todas las cuentas con sus saldos iniciales, movimientos del período y saldos finales.',
      icon: 'bi-table',
      color: 'info',
      category: 'Reporte Contable',
      features: ['Saldo inicial', 'Movimientos del período', 'Saldo final', 'Verificación contable']
    },
    {
      id: 'auxiliar-cuenta',
      title: 'Auxiliar de Cuenta',
      description: 'Detalle completo de movimientos de una cuenta contable específica en un período determinado.',
      icon: 'bi-file-earmark-text',
      color: 'warning',
      category: 'Reporte de Detalle',
      features: ['Movimientos detallados', 'Saldo inicial y final', 'Referencias', 'Drill-down desde otros reportes']
    },
    {
      id: 'transactions',
      title: 'Transacciones Contables',
      description: 'Lista completa de todas las transacciones contables con filtros avanzados y opciones de exportación.',
      icon: 'bi-journal-text',
      color: 'secondary',
      category: 'Reporte de Detalle',
      features: ['Filtros avanzados', 'Resumen de totales', 'Exportación', 'Contabilización masiva']
    },
    {
      id: 'financial-transactions',
      title: 'Transacciones Financieras',
      description: 'Lista de transacciones bancarias y financieras con filtros por cuenta, tipo y estado de conciliación.',
      icon: 'bi-bank',
      color: 'danger',
      category: 'Reporte de Detalle',
      features: ['Filtros por cuenta bancaria', 'Resumen por tipo', 'Estado de conciliación', 'Exportación']
    }
  ]

  const handleReportClick = (reportId) => {
    if (onSelectReport) {
      onSelectReport(reportId)
    }
  }

  const getCategoryColor = (category) => {
    const colors = {
      'Reporte Financiero': 'primary',
      'Reporte Contable': 'info',
      'Reporte de Detalle': 'warning'
    }
    return colors[category] || 'secondary'
  }

  return (
    <div>
      <Card className="mb-4">
        <Card.Header>
          <h4 className="mb-0">
            <i className="bi bi-clipboard-data me-2"></i>
            Dashboard de Reportes
          </h4>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-4">
            Seleccione un reporte para generar y visualizar información financiera y contable de su empresa.
            Todos los reportes incluyen opciones de exportación a PDF y Excel.
          </p>

          <Row className="g-4">
            {reports.map((report) => (
              <Col key={report.id} xs={12} md={6} lg={4}>
                <Card 
                  className="h-100 shadow-sm hover-card" 
                  style={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onClick={() => handleReportClick(report.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <Card.Body>
                    <div className="d-flex align-items-start mb-3">
                      <div className={`bg-${report.color} bg-opacity-10 rounded p-3 me-3`}>
                        <i className={`bi ${report.icon} text-${report.color}`} style={{ fontSize: '2rem' }}></i>
                      </div>
                      <div className="flex-grow-1">
                        <Card.Title className="h5 mb-1">{report.title}</Card.Title>
                        <Badge bg={getCategoryColor(report.category)} className="mb-2">
                          {report.category}
                        </Badge>
                      </div>
                    </div>
                    <Card.Text className="text-muted small mb-3">
                      {report.description}
                    </Card.Text>
                    <div className="mb-3">
                      <small className="text-muted d-block mb-1"><strong>Características:</strong></small>
                      <ul className="small mb-0 ps-3">
                        {report.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                    <Button 
                      variant={report.color} 
                      className="w-100"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleReportClick(report.id)
                      }}
                    >
                      <i className="bi bi-arrow-right-circle me-2"></i>
                      Abrir Reporte
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      {/* Información adicional */}
      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header className="bg-light">
              <h6 className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                Tipos de Reportes
              </h6>
            </Card.Header>
            <Card.Body>
              <ul className="mb-0">
                <li><strong>Reportes Financieros:</strong> Estado de Resultados y Balance General</li>
                <li><strong>Reportes Contables:</strong> Balanza de Comprobación</li>
                <li><strong>Reportes de Detalle:</strong> Auxiliar de Cuenta y Transacciones</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header className="bg-light">
              <h6 className="mb-0">
                <i className="bi bi-lightbulb me-2"></i>
                Consejos de Uso
              </h6>
            </Card.Header>
            <Card.Body>
              <ul className="mb-0">
                <li>Use <strong>Ctrl+Enter</strong> para generar reportes rápidamente</li>
                <li>Use <strong>Ctrl+S</strong> para exportar a Excel</li>
                <li>Haga clic en conceptos o cuentas para ver detalles (drill-down)</li>
                <li>Todos los reportes se pueden exportar a PDF o Excel</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default ReportsDashboard
